import React, { useState, useEffect, useRef } from "react";
import { AppState } from "@/types/linguadeck";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Check, Settings as SettingsIcon, Save, X, Trash2, Plus } from "lucide-react";
import { useThemeStore } from "@/stores/useThemeStore";
import { useGamificationStore } from "@/stores/useGamificationStore";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { updateProfileAvatar } from "@/lib/db";
import { getStoredGeminiKeys, getStoredPollinationsKeys, normalizeApiKeys } from "@/lib/apiKeys";
import LanguageSwitcher from "./LanguageSwitcher";

interface SettingsModalProps {
  state: AppState;
  onSave: (updates: Partial<AppState>) => void;
  onClose: () => void;
}

const modelDescs: Record<string, string> = {
  "gemini-fast": "settings.modelDesc.geminiFast",
  "gemini-search": "settings.modelDesc.geminiSearch",
  openai: "settings.modelDesc.openai",
  "openai-fast": "settings.modelDesc.openaiFast",
  mistral: "settings.modelDesc.mistral",
};

export default function SettingsModal({ state, onSave, onClose }: SettingsModalProps) {
  const { t } = useTranslation();
  const { user, session, profile, avatarUrl, refreshProfile, isAdminMode } = useAuth();
  const systemTheme = useThemeStore((s) => s.systemTheme);
  const setSystemTheme = useThemeStore((s) => s.setSystemTheme);
  const clearEquippedTheme = useGamificationStore((s) => s.clearEquippedTheme);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [aiModel, setAiModel] = useState(state.aiModel);
  const [pollinationsKeys, setPollinationsKeys] = useState<string[]>(() =>
    Array.isArray(state.pollinationsKey) ? state.pollinationsKey : (state.pollinationsKey ? [state.pollinationsKey] : [])
  );
  const [geminiKeys, setGeminiKeys] = useState<string[]>(() =>
    Array.isArray(state.geminiKey) ? state.geminiKey : (state.geminiKey ? [state.geminiKey] : [])
  );
  const [theme, setTheme] = useState(systemTheme);
  const [avatarUploading, setAvatarUploading] = useState(false);
  useEffect(() => {
    setTheme(systemTheme);
  }, [systemTheme]);

  // Hydrate from saved local keys first, then from admin-shared backend settings.
  useEffect(() => {
    const localGeminiKeys = getStoredGeminiKeys();
    const localPollinationsKeys = getStoredPollinationsKeys();
    if (localGeminiKeys.length > 0 && geminiKeys.every((k) => !k?.trim())) setGeminiKeys(localGeminiKeys);
    if (localPollinationsKeys.length > 0 && pollinationsKeys.every((k) => !k?.trim())) setPollinationsKeys(localPollinationsKeys);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSharedAdminAiSettings = async () => {
      if (!isAdminMode || !session?.access_token) return;
      try {
        const res = await fetch("/api/admin/ai-settings", {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        const raw = await res.text();
        const data = raw ? (JSON.parse(raw) as { geminiKeys?: string[]; pollinationsKeys?: string[]; error?: string }) : {};
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (cancelled) return;

        const sharedGeminiKeys = normalizeApiKeys(data.geminiKeys);
        const sharedPollinationsKeys = normalizeApiKeys(data.pollinationsKeys);
        if (sharedGeminiKeys.length > 0) setGeminiKeys(sharedGeminiKeys);
        if (sharedPollinationsKeys.length > 0) setPollinationsKeys(sharedPollinationsKeys);
      } catch (err) {
        console.warn("[Settings] Failed to load shared AI settings:", err);
      }
    };

    loadSharedAdminAiSettings();

    return () => {
      cancelled = true;
    };
  }, [isAdminMode, session?.access_token]);

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      toast.error(t("settings.avatarInvalidType"));
      return;
    }
    const MAX_SIZE_MB = 5;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(t("settings.avatarTooLarge", { max: MAX_SIZE_MB }));
      return;
    }
    setAvatarUploading(true);
    try {
      const path = `${user.id}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: false });
      if (uploadError) {
        console.error("[Settings] Avatar storage upload error:", uploadError.message, uploadError);
        throw uploadError;
      }
      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const publicUrl = urlData.publicUrl;
      await updateProfileAvatar(user.id, publicUrl);
      await refreshProfile();
      console.log("[Settings] Avatar uploaded and profile updated:", publicUrl);
      toast.success(t("settings.avatarUpdated"));
    } catch (err) {
      console.error("[Settings] Avatar upload failed:", err);
      toast.error(t("settings.avatarUploadFailed"));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async () => {
    const safePollinationsKeys = normalizeApiKeys(pollinationsKeys);
    const safeGeminiKeys = normalizeApiKeys(geminiKeys);

    localStorage.setItem("ld_pollinations", JSON.stringify(safePollinationsKeys));
    localStorage.setItem("ld_gemini", JSON.stringify(safeGeminiKeys));
    const firstGemini = safeGeminiKeys[0];
    if (firstGemini) localStorage.setItem("gemini_api_key", firstGemini);
    else localStorage.removeItem("gemini_api_key");
    const firstPoll = safePollinationsKeys[0];
    if (firstPoll) localStorage.setItem("pollinations_api_key", firstPoll);
    else localStorage.removeItem("pollinations_api_key");
    localStorage.setItem("ld_model", aiModel);

    if (isAdminMode && session?.access_token) {
      const res = await fetch("/api/admin/ai-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          geminiKeys: safeGeminiKeys,
          pollinationsKeys: safePollinationsKeys,
        }),
      });

      const raw = await res.text();
      const data = raw ? (JSON.parse(raw) as { error?: string }) : {};
      if (!res.ok) {
        toast.error(data.error || t("shared.error", "Error"));
        return;
      }
    }

    onSave({ aiModel, pollinationsKey: safePollinationsKeys, geminiKey: safeGeminiKeys, theme });
    onClose();
  };

  const addGeminiKey = () => setGeminiKeys((prev) => [...prev, ""]);
  const removeGeminiKey = (index: number) => setGeminiKeys((prev) => prev.filter((_, i) => i !== index));
  const setGeminiKeyAt = (index: number, value: string) =>
    setGeminiKeys((prev) => prev.map((k, i) => (i === index ? value : k)));

  const addPollinationsKey = () => setPollinationsKeys((prev) => [...prev, ""]);
  const removePollinationsKey = (index: number) => setPollinationsKeys((prev) => prev.filter((_, i) => i !== index));
  const setPollinationsKeyAt = (index: number, value: string) =>
    setPollinationsKeys((prev) => prev.map((k, i) => (i === index ? value : k)));

  const themes = [
    { id: "dark" as const, label: t("shared.themeDark"), bg: "#0a0a0f" },
    { id: "light" as const, label: t("shared.themeLight"), bg: "#f5f5f8" },
    { id: "system" as const, label: t("shared.themeSystem"), bg: "linear-gradient(135deg,#f5f5f8 50%,#0a0a0f 50%)" },
  ];

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-box"
        style={{ background: "hsl(var(--bg2))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", padding: 28, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}
      >
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="flex items-center gap-2">
            <SettingsIcon size={20} className="shrink-0" />
            {t("buttons.settings")}
          </span>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} aria-label={t("shared.close", "Close")}>
            <X size={20} />
          </button>
        </div>

        {/* Avatar: display current or placeholder, click to upload */}
        {user && (
          <Field label={t("settings.avatar")}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              aria-hidden
              onChange={handleAvatarFile}
              disabled={avatarUploading}
            />
            <div className="relative inline-block w-fit">
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={avatarUploading}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "2px solid hsl(var(--border))",
                  background: avatarUrl ? "transparent" : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
                  cursor: avatarUploading ? "wait" : "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label={t("settings.changeAvatar")}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span style={{ fontSize: 28, fontWeight: 700, color: "white" }}>
                    {(profile?.username ?? user.email?.[0] ?? "U").toString().charAt(0).toUpperCase()}
                  </span>
                )}
              </button>
              {/* Camera badge: absolute so it sits on the circle edge */}
              <span
                className="absolute bottom-1 right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-900"
                style={{ background: "hsl(var(--primary))" }}
              >
                {avatarUploading ? (
                  <span className="text-white text-[10px]">...</span>
                ) : (
                  <Camera size={14} className="text-white" strokeWidth={2.5} />
                )}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 6 }}>
              {t("settings.avatarHint")}
            </div>
          </Field>
        )}

        {/* API Keys — visible only when active role is Admin (isAdminMode); hidden for Teacher/Student */}
        {isAdminMode && (
          <>
            <Field label={t("settings.api.gemini_label", "Gemini API Key (for card explanations)")}>
              {geminiKeys.map((key, index) => (
                <div key={index} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <input
                    type="password"
                    value={key}
                    onChange={(e) => setGeminiKeyAt(index, e.target.value)}
                    placeholder="AIza..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeGeminiKey(index)}
                    style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    aria-label={t("shared.remove", "Remove")}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addGeminiKey}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "1px dashed hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", fontSize: 13, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}
              >
                <Plus size={16} />
                {t("settings.add_key")}
              </button>
              <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 4 }}>{t("settings.api.gemini_hint", "Optional. Uses server key if not set.")}</div>
            </Field>

            <Field label={t("settings.api.pollinations_label", "Pollinations API Key (for image generation)")}>
              {pollinationsKeys.map((key, index) => (
                <div key={index} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <input
                    type="password"
                    value={key}
                    onChange={(e) => setPollinationsKeyAt(index, e.target.value)}
                    placeholder="sk_..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removePollinationsKey(index)}
                    style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    aria-label={t("shared.remove", "Remove")}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addPollinationsKey}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "1px dashed hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", fontSize: 13, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}
              >
                <Plus size={16} />
                {t("settings.add_key")}
              </button>
              <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 4 }}>{t("settings.api.pollinations_hint", "Optional. Uses public API if not set.")}</div>
            </Field>
          </>
        )}

        {isAdminMode && (
          <>
            <div style={{ background: "rgba(0,229,160,.08)", border: "1px solid rgba(0,229,160,.2)", borderRadius: "var(--radius-sm)", padding: "12px 14px", marginBottom: 16, fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Check size={18} className="shrink-0 text-emerald-600 mt-0.5" />
              <span><b>Google Gemini</b> — {t("settings.geminiDescription")}</span>
            </div>

            <Field label={t("settings.aiModel")}>
              <select value={aiModel} onChange={(e) => setAiModel(e.target.value)} style={inputStyle}>
                <option value="gemini-fast">{t("settings.geminiRecommended")}</option>
                <option value="gemini-search">{t("settings.geminiSearch")}</option>
                <option value="openai">{t("settings.gptMini")}</option>
                <option value="openai-fast">{t("settings.gptNano")}</option>
                <option value="mistral">{t("settings.mistralModel")}</option>
              </select>
              <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 4 }}>{t(modelDescs[aiModel])}</div>
            </Field>
          </>
        )}

        <Field label={t("settings.language")}>
          <LanguageSwitcher />
        </Field>

        <Field label={t("settings.theme")}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {themes.map((th) => (
              <div
                key={th.id}
                onClick={() => {
                  clearEquippedTheme();
                  setSystemTheme(th.id);
                  setTheme(th.id);
                }}
                style={{
                  border: `2px solid ${theme === th.id ? "hsl(var(--primary))" : "hsl(var(--border))"}`,
                  borderRadius: "var(--radius-sm)",
                  padding: "12px 8px",
                  textAlign: "center",
                  cursor: "pointer",
                  fontSize: 12,
                  transition: "all .2s",
                  color: "hsl(var(--foreground))",
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, margin: "0 auto 6px", background: th.bg }} />
                {th.label}
              </div>
            ))}
          </div>
        </Field>

        <button
          onClick={handleSave}
          style={{ width: "100%", padding: "10px 16px", borderRadius: 50, border: "none", background: "hsl(var(--primary))", color: "white", fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer", marginTop: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
        >
          <Save size={16} className="shrink-0" />
          {t("buttons.save")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "hsl(var(--muted-foreground))", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "hsl(var(--bg3))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "var(--radius-sm)",
  padding: "10px 14px",
  color: "hsl(var(--foreground))",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 14,
  outline: "none",
};
